import {
  MAX_LOCALIZED_LENGTH,
  canonicalLanguageTag,
  normalizeLocalizedText,
  type LanguageTag,
  type LocalizedFieldPolicy,
  type LocalizedText,
} from '@/shared/i18n/localizedText'

/**
 * THE DRAFT OF ONE LOCALIZED VALUE, headless.
 *
 * A draft has the value's own shape — `sourceLanguage` plus a map of
 * translations — but it is SPARSE: a language just added has no text yet, and
 * the original may be blank while a form is still being filled. The kernel in
 * `shared/i18n/localizedText.ts` refuses such a value at the write boundary,
 * which is right for a write and wrong for an editor; so the draft holds what
 * is typed, and `validateLocalizedDraft` is the one place it becomes the value
 * a form may send. Every step here is a pure function over an immutable draft
 * — nothing persists, nothing asks the server, and the form that owns Save
 * keeps its HTTP, its permission and its revision.
 *
 * TWO THINGS A DRAFT CANNOT DO, by construction rather than by a check the
 * component remembers to make: it cannot lose its original — the source
 * language is not removable, and a suggestion never rewrites it — and it
 * cannot change its original by accident, because `makeDraftOriginal` is the
 * only step that moves `sourceLanguage`, and it moves it to a language that
 * already carries text.
 */
export type LocalizedDraft = LocalizedText

/**
 * What a domain hands over: the stored value as its API types it, tags as
 * plain strings. The draft canonicalizes them on the way in, so a consumer
 * never has to brand a tag itself.
 */
export interface StoredLocalizedText {
  sourceLanguage: string
  values: Readonly<Record<string, string>>
}

/** Admit and canonicalize a field through its domain policy; invalid values throw. */
export function readStoredLocalizedText(raw: unknown, policy: LocalizedFieldPolicy): LocalizedText | null {
  return normalizeLocalizedText(raw, policy)
}

/** Admit a stored field at a domain's read boundary using that domain's policy. */
export function isStoredLocalizedText(raw: unknown, policy: LocalizedFieldPolicy): raw is StoredLocalizedText {
  try {
    return readStoredLocalizedText(raw, policy) !== null
  } catch {
    return false
  }
}

export type LocalizedDraftRefusal =
  /** The tag is not a BCP 47 language the kernel accepts. */
  | 'invalid-language'
  /** The language is already carried, once its spelling is canonical. */
  | 'language-present'
  /** The language is not carried, so there is nothing to edit or remove. */
  | 'language-absent'
  /** The original stays: a value without its source is not a value. */
  | 'source-not-removable'
  /** Only a language with text can become the original. */
  | 'blank-original'
  /** The domain's policy caps how many languages one value carries. */
  | 'too-many-languages'

export type LocalizedDraftStep =
  | { ok: true; draft: LocalizedDraft }
  | { ok: false; refusal: LocalizedDraftRefusal; draft: LocalizedDraft }

/** Blank is the Unicode White_Space property, exactly as the kernel reads it. */
export function isBlankText(text: string): boolean {
  return !/[^\p{White_Space}]/u.test(text)
}

const refused = (refusal: LocalizedDraftRefusal, draft: LocalizedDraft): LocalizedDraftStep => ({
  ok: false,
  refusal,
  draft,
})

const withValues = (draft: LocalizedDraft, values: Record<LanguageTag, string>): LocalizedDraft => ({
  sourceLanguage: draft.sourceLanguage,
  values,
})

function tagOf(raw: string): LanguageTag | null {
  try {
    return canonicalLanguageTag(raw)
  } catch {
    return null
  }
}

/**
 * The draft a stored value opens as, or the empty draft a field with nothing
 * stored opens as — authored in the language the DOMAIN names, never a hidden
 * English default.
 */
export function localizedDraftFrom(saved: StoredLocalizedText | null, defaultSourceLanguage: string): LocalizedDraft {
  if (saved === null) {
    const source = canonicalLanguageTag(defaultSourceLanguage)
    return { sourceLanguage: source, values: { [source]: '' } }
  }
  const source = canonicalLanguageTag(saved.sourceLanguage)
  const values: Record<LanguageTag, string> = {}
  for (const [key, text] of Object.entries(saved.values)) {
    values[canonicalLanguageTag(key)] = text
  }
  values[source] ??= ''
  return { sourceLanguage: source, values }
}

/** The original first, then every translation in ASCII order of its tag. */
export function draftLanguages(draft: LocalizedDraft): LanguageTag[] {
  const others = (Object.keys(draft.values) as LanguageTag[])
    .filter((tag) => tag !== draft.sourceLanguage)
    .sort()
  return [draft.sourceLanguage, ...others]
}

export function addDraftLanguage(
  draft: LocalizedDraft,
  raw: string,
  policy: LocalizedFieldPolicy,
): LocalizedDraftStep {
  const tag = tagOf(raw)
  if (tag === null) return refused('invalid-language', draft)
  if (Object.hasOwn(draft.values, tag)) return refused('language-present', draft)
  if (Object.keys(draft.values).length >= policy.maxLanguages) return refused('too-many-languages', draft)
  return { ok: true, draft: withValues(draft, { ...draft.values, [tag]: '' }) }
}

export function editDraftValue(draft: LocalizedDraft, tag: LanguageTag, text: string): LocalizedDraftStep {
  if (!Object.hasOwn(draft.values, tag)) return refused('language-absent', draft)
  return { ok: true, draft: withValues(draft, { ...draft.values, [tag]: text }) }
}

export function removeDraftLanguage(draft: LocalizedDraft, tag: LanguageTag): LocalizedDraftStep {
  if (tag === draft.sourceLanguage) return refused('source-not-removable', draft)
  if (!Object.hasOwn(draft.values, tag)) return refused('language-absent', draft)
  const values = { ...draft.values }
  delete values[tag]
  return { ok: true, draft: withValues(draft, values) }
}

/**
 * The explicit act that moves the original. The former original stays as a
 * translation when it has text; a blank former original is dropped, because a
 * row saying "type the translation or remove the language" for text nobody
 * wrote is noise rather than guidance.
 */
export function makeDraftOriginal(draft: LocalizedDraft, tag: LanguageTag): LocalizedDraftStep {
  if (!Object.hasOwn(draft.values, tag)) return refused('language-absent', draft)
  if (isBlankText(draft.values[tag] ?? '')) return refused('blank-original', draft)
  if (tag === draft.sourceLanguage) return { ok: true, draft }
  const values = { ...draft.values }
  if (isBlankText(values[draft.sourceLanguage] ?? '')) {
    delete values[draft.sourceLanguage]
  }
  return { ok: true, draft: { sourceLanguage: tag, values } }
}

/**
 * Put suggested text into the draft — into the draft ONLY. A suggestion fills
 * a language that is absent or blank; it never rewrites the original and never
 * overwrites text somebody typed, so a slow answer cannot land over a newer
 * edit. What could not be placed is returned by the tag it arrived under.
 */
export function insertDraftSuggestions(
  draft: LocalizedDraft,
  suggestions: Readonly<Record<string, string>>,
  policy: LocalizedFieldPolicy,
): { draft: LocalizedDraft; skipped: string[] } {
  const values = { ...draft.values }
  const skipped: string[] = []
  for (const [raw, text] of Object.entries(suggestions)) {
    const tag = tagOf(raw)
    if (tag === null || tag === draft.sourceLanguage || isBlankText(text)) {
      skipped.push(raw)
      continue
    }
    const present = Object.hasOwn(values, tag)
    if (present && !isBlankText(values[tag] ?? '')) {
      skipped.push(raw)
      continue
    }
    if (!present && Object.keys(values).length >= policy.maxLanguages) {
      skipped.push(raw)
      continue
    }
    values[tag] = text
  }
  return { draft: withValues(draft, values), skipped }
}

/** Offered languages the draft does not carry yet — what an "add" control lists. */
export function absentDraftLanguages(draft: LocalizedDraft, offered: readonly string[]): LanguageTag[] {
  return canonicalOffered(offered).filter((tag) => !Object.hasOwn(draft.values, tag))
}

/**
 * Offered languages with no text yet: absent, or carried but blank. What a
 * "suggest all" asks for, so a language the reader added and left empty is
 * filled rather than skipped.
 */
export function missingDraftLanguages(draft: LocalizedDraft, offered: readonly string[]): LanguageTag[] {
  return canonicalOffered(offered).filter(
    (tag) => tag !== draft.sourceLanguage && isBlankText(draft.values[tag] ?? ''),
  )
}

function canonicalOffered(offered: readonly string[]): LanguageTag[] {
  return [...new Set(offered.map((raw) => canonicalLanguageTag(raw)))]
}

export type LocalizedDraftProblemKind =
  | 'source-required'
  | 'blank'
  | 'too-long'
  | 'invalid-text'
  | 'too-many-languages'

export interface LocalizedDraftProblem {
  /** The language the problem sits under, or null for the value as a whole. */
  language: LanguageTag | null
  kind: LocalizedDraftProblemKind
}

export interface LocalizedDraftValidation {
  /** The canonical value a form may send, or null while a problem stands or the optional value is absent. */
  value: LocalizedText | null
  problems: LocalizedDraftProblem[]
}

/**
 * Where a draft becomes a value. The rules are the kernel's — the probe below
 * asks `normalizeLocalizedText` itself rather than restating its length and
 * character rules — and what this adds is ATTRIBUTION: which language the
 * refusal is about, so an error can stand under the control it names.
 *
 * An optional value that carries no text at all is absent, not invalid.
 */
export function validateLocalizedDraft(draft: LocalizedDraft, policy: LocalizedFieldPolicy): LocalizedDraftValidation {
  const entries = Object.entries(draft.values) as [LanguageTag, string][]
  const problems: LocalizedDraftProblem[] = []
  if (entries.every(([, text]) => isBlankText(text))) {
    if (policy.required) problems.push({ language: draft.sourceLanguage, kind: 'source-required' })
    return { value: null, problems }
  }
  for (const [tag, text] of entries) {
    if (isBlankText(text)) {
      problems.push({ language: tag, kind: tag === draft.sourceLanguage ? 'source-required' : 'blank' })
      continue
    }
    const kind = textProblem(text, policy.maxLength)
    if (kind !== null) problems.push({ language: tag, kind })
  }
  if (entries.length > policy.maxLanguages) problems.push({ language: null, kind: 'too-many-languages' })
  if (problems.length > 0) return { value: null, problems }
  return { value: normalizeLocalizedText(draft, policy), problems: [] }
}

const PROBE_TAG = 'en' as LanguageTag

function textProblem(text: string, maxLength: number): 'too-long' | 'invalid-text' | null {
  const admitted = (limit: number) => {
    try {
      normalizeLocalizedText(
        { sourceLanguage: PROBE_TAG, values: { [PROBE_TAG]: text } },
        { required: true, maxLanguages: 1, maxLength: limit },
      )
      return true
    } catch {
      return false
    }
  }
  if (admitted(maxLength)) return null
  return admitted(MAX_LOCALIZED_LENGTH) ? 'too-long' : 'invalid-text'
}

/** Two drafts carry the same original and the same text under the same tags. */
export function sameLocalizedDraft(left: LocalizedDraft, right: LocalizedDraft): boolean {
  if (left.sourceLanguage !== right.sourceLanguage) return false
  const leftKeys = Object.keys(left.values).sort()
  const rightKeys = Object.keys(right.values).sort()
  return (
    leftKeys.length === rightKeys.length &&
    leftKeys.every((key, index) => key === rightKeys[index] && left.values[key as LanguageTag] === right.values[key as LanguageTag])
  )
}
